import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import CustomLayout from "../../Layout/CustomLayout";
import {
  Typography,
  Card,
  Row,
  Col,
  DatePicker,
  Image,
  Table,
  Input,
  Space,
  Select,
  Tag,
  Button,
  message,
} from "antd";
import { SearchOutlined, CloudDownloadOutlined } from "@ant-design/icons";
import SideMenu from "../../Layout/CustomLayout/SideMenu";
import typoStyles from "../../../styles/Custom/Typography.module.css";
import NoDataPage from "../../NoDataPages/NoDataPage";
import investigationImage from "../../../assets/images/nurseIcons/investigation.svg";
import inputStyles from "../../../styles/Custom/Input.module.css";
import calendarImg from "../../../assets/images/nurseIcons/calender.svg";

import { getOPReportAPI } from "../../../services/orgDashboard.services";
import { getFacilityListApi } from "../../../services/facilities.services";
import { getCookie } from "../../../helpers/localStorage";
import { FacilityID, Role } from "../../../constants/defaultKeys";
import axiosInstance from "../../../config/axios.config";
import useQuery from "../../../hooks/useQuery";
import {
  parsePageParam,
  parseSearchParam,
  updateUrlSearchParams,
} from "../../../helpers/urlFilters";

import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
dayjs.extend(isoWeek);

const { RangePicker } = DatePicker;
const dateFormat = "YYYY-MM-DD";
const endOfMonth = dayjs().endOf("month");

const OPDashboard = () => {
  const navigate = useNavigate();
  const params = useQuery();
  const facilityAdmin =
    getCookie(Role) === "Facility_Admin" ? getCookie(FacilityID) : null;

  const searchText = parseSearchParam(params) || "";
  const page = parsePageParam(params);
  const currentFacility = facilityAdmin
    ? Number(facilityAdmin)
    : params?.facility
      ? Number(params.facility)
      : null;
  const date = {
    start_date: params?.start_date || "",
    end_date: params?.end_date || "",
  };

  const [searchInput, setSearchInput] = useState(searchText);
  const [facilityData, setFacilityData] = useState([]);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: page,
    pageSize: 100,
    total: 0,
  });
  const debounceRef = useRef(null);

  useEffect(() => {
    setSearchInput(searchText);
  }, [searchText]);

  useEffect(() => {
    setPagination((prev) => ({ ...prev, current: page }));
  }, [page]);

  useEffect(() => {
    getFacilityListApi()
      .then((res) => {
        const opts = res?.data?.map((f) => ({
          label: f?.name,
          value: f?.id,
        }));
        setFacilityData([{ label: "All", value: null }, ...opts]);
      })
      .catch((e) => console.log(e));
  }, []);

  const onDateChange = (range) => {
    if (!range?.[0] || !range?.[1]) return;
    updateUrlSearchParams(navigate, {
      start_date: dayjs(range[0]).format(dateFormat),
      end_date: dayjs(range[1]).format(dateFormat),
      page: 1,
    });
  };

  const fetchOPReport = async (pageNum = page, pageSize = 100) => {
    setLoading(true);
    try {
      const res = await getOPReportAPI(
        currentFacility,
        date,
        pageNum,
        pageSize,
        searchText
      );

      setData(res?.data?.data || []);
      setPagination({
        current: res?.data?.current_page || pageNum,
        pageSize,
        total: res?.data?.total_count || 0,
      });
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const handleTableChange = (nextPagination) => {
    updateUrlSearchParams(navigate, { page: nextPagination.current });
    fetchOPReport(nextPagination.current, nextPagination.pageSize);
  };

  const handleSearchChange = (value) => {
    setSearchInput(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      updateUrlSearchParams(navigate, { search: value || null, page: 1 });
    }, 1000);
  };

  useEffect(() => {
    if (date.start_date && date.end_date) {
      fetchOPReport(page, pagination.pageSize);
    }
    // eslint-disable-next-line
  }, [currentFacility, date.start_date, date.end_date, page, searchText]);

  const toTitleCase = (text = "") =>
    text
      .toLowerCase()
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");

  const generateColumns = () => {
    if (!data || data.length === 0) return [];

    const sample = data[0];
    const keys = Object.keys(sample);

    const customRender = {
      consultation_date: (value) =>
        value ? dayjs(value).format("MMM DD, YYYY") : "-",
      patient_name: (value) => (value ? toTitleCase(value) : "-"),
    };

    return keys.map((key) => ({
      title: key
        .toLowerCase()
        .split("_")
        .map((s) => {
          if (s === "id" || s === "uid") return s.toUpperCase();
          return s.charAt(0).toUpperCase() + s.slice(1);
        })
        .join(" "),
      dataIndex: key,
      key,
      width: 150,
      render: customRender[key.toLowerCase()] || undefined,
      fixed: key === "UID" ? "left" : undefined,
    }));
  };

  const columns = generateColumns();

  const handleExport = async (type) => {
    if (!date.start_date || !date.end_date) {
      message.error("Please select the date filters to download the file");
      return;
    }

    try {
      const exportParams = {
        facility: currentFacility,
        start_date: date.start_date,
        end_date: date.end_date,
        response_type: type,
        search: searchText || undefined,
      };

      Object.keys(exportParams).forEach(
        (k) => exportParams[k] == null && delete exportParams[k]
      );

      const query = new URLSearchParams(exportParams).toString();

      axiosInstance({
        url: `/billing/dashboard/op-report/?${query}`,
        responseType: "blob",
      }).then((res) => {
        const blob = res.data;
        const file = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = file;
        a.download = `op_report.${type}`;
        a.click();
        window.URL.revokeObjectURL(file);
      });
    } catch (err) {
      console.log(err);
    }
  };

  return (
    <CustomLayout
      header={<Typography className={typoStyles.h6}>Outpatients</Typography>}
      sideContent={<SideMenu />}
    >
      <Card
        style={{ margin: 16 }}
        title={
          <Space>
            <Typography.Text style={{ fontSize: 20, fontWeight: 600 }}>
              Outpatients
            </Typography.Text>
            {date.start_date && date.end_date && (
              <Tag color="purple">
                {dayjs(date.start_date).format("MMM DD, YYYY")} to{" "}
                {dayjs(date.end_date).format("MMM DD, YYYY")}
              </Tag>
            )}
          </Space>
        }
      >
        <Row gutter={8} align="middle" style={{ marginBottom: 16 }}>
          {!facilityAdmin && (
            <Col>
              <div style={{ fontWeight: 500, marginBottom: 8 }}>Facility</div>
              <Select
                value={currentFacility}
                options={facilityData}
                onChange={(val) =>
                  updateUrlSearchParams(navigate, { facility: val, page: 1 })
                }
                size="large"
                style={{ width: 150 }}
              />
            </Col>
          )}

          <Col>
            <div style={{ fontWeight: 500, marginBottom: 8 }}>
              Start Date & End Date
            </div>
            <RangePicker
              className={inputStyles.mdDateInput}
              onChange={onDateChange}
              allowClear={false}
              disabledDate={(current) => current && current > endOfMonth}
              value={[
                date.start_date ? dayjs(date.start_date) : null,
                date.end_date ? dayjs(date.end_date) : null,
              ]}
              suffixIcon={<Image width={20} src={calendarImg} preview={false} />}
              style={{
                height: 38,
                border:
                  "1px solid color-mix(in srgb, var(--ant-primary-color) 24%, white)",
              }}
            />
          </Col>

          <Col flex="auto" />

          <Col>
            <Input
              placeholder="Search Outpatients"
              prefix={<SearchOutlined />}
              style={{ width: 220, borderRadius: 8, marginTop: 24 }}
              allowClear
              value={searchInput}
              onChange={(e) => handleSearchChange(e.target.value)}
            />
          </Col>
          <Col>
            <Space style={{ marginTop: 24 }}>
              <Button
                icon={<CloudDownloadOutlined />}
                style={{
                  background:
                    "color-mix(in srgb, var(--ant-primary-color) 10%, white)",
                  color: "var(--ant-primary-color)",
                  borderRadius: 8,
                }}
                onClick={() => handleExport("xlsx")}
              >
                Excel
              </Button>

              <Button
                icon={<CloudDownloadOutlined />}
                type="primary"
                onClick={() => handleExport("csv")}
              >
                CSV
              </Button>
            </Space>
          </Col>
        </Row>
        <Table
          columns={columns}
          dataSource={data}
          loading={loading}
          rowKey="visit_id"
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: false,
            position: ["bottomCenter"],
          }}
          onChange={handleTableChange}
          scroll={{ x: "max-content", y: "calc(100vh - 320px)" }}
          locale={{
            emptyText: (
              <NoDataPage
                title="No Data Found"
                subtitle="Select a date range to view data"
                image={investigationImage}
              />
            ),
          }}
        />
      </Card>
    </CustomLayout>
  );
};

export default OPDashboard;
